/**
 * Company-level acknowledgement bridge.
 *
 * When one teammate taps a push, the service worker posts a message (or the
 * opened URL carries `?ack=`). We stamp the acknowledgement so the rest of the
 * team stops being nagged about an event somebody already picked up.
 *
 * Deliberately an EVENT, not a setting: nobody's preferences change, the noise
 * just stops. Same pattern as an incident ack in PagerDuty or a shared-inbox
 * assignment in Front.
 */
import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { NOTIFICATIONS_KEY } from '@/hooks/useNotifications';
import { trackActivity } from '@/lib/activityTracking';

const PushAckBridge = () => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!user) return;

    const acknowledge = async (id: string | null | undefined) => {
      if (!id) return;
      const { error } = await supabase.rpc('acknowledge_notification', {
        _notification_id: id,
      });
      if (error) {
        console.error('[push] acknowledge failed', error);
        return;
      }
      queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
    };

    // Path A: the SW focused an already-open tab and told us which event.
    const onMessage = (event: MessageEvent) => {
      const data = event.data as {
        type?: string;
        notificationId?: string;
        href?: string;
        category?: string;
      } | null;
      if (!data || data.type !== 'cfx-push-ack') return;
      trackActivity('push_notification_clicked', {
        category: data.category ?? null,
        href: data.href ?? null,
        path: 'message',
      });
      void acknowledge(data.notificationId);
      if (data.href && data.href !== location.pathname + location.search) {
        navigate(data.href);
      }
    };

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', onMessage);
    }

    // Path B: the SW opened a fresh window carrying ?ack=<id>.
    const params = new URLSearchParams(location.search);
    const ackId = params.get('ack');
    if (ackId) {
      trackActivity('push_notification_clicked', {
        category: params.get('cat'),
        href: location.pathname,
        path: 'url_ack',
      });
      void acknowledge(ackId);
      params.delete('ack');
      params.delete('cat');
      const rest = params.toString();
      navigate(location.pathname + (rest ? `?${rest}` : ''), { replace: true });
    }

    return () => {
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('message', onMessage);
      }
    };
  }, [user, location.pathname, location.search, navigate, queryClient]);

  return null;
};

export default PushAckBridge;
